import { ReactElement, createContext } from "react";

export type OpenPosition =
  | "bottom-start"
  | "bottom-end"
  | "bottom-center"
  | "left-end"
  | "left-start"
  | "top-start"
  | "top-end"
  | "top-center"
  | "right-start"
  | "right-end";

/**The position of the menu's open section */
export type BubbleCoordinates = {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
};

/** Closes the menu, then runs optional work after its native modal has dismissed. */
export type CloseMenu = (afterClose?: () => void) => void;

export type MenuProps = {
  trigger: (args: { onPress: () => void; isOpen: boolean }) => ReactElement;
  /**
   *
   * The inner content of the menu. Exposes a close Menu function so that you can
   * deal with closing on click if you need
   */
  content: ({
    closeMenu,
  }: {
    closeMenu: CloseMenu;
    triggerWidth: number;
  }) => ReactElement;
  /**
   * Controls the position of the opened menu bubble in relation to
   * the trigger
   */
  openPosition?: OpenPosition;
  /**
   * Will override any internal open state. Useful if you want to
   * open the menu from an external source. But if you set this to something
   * other than undefined, you need to take external control of the state
   */
  overrideIsOpen?: boolean;
  /**
   *
   * Fires whenever the menu attempts to close, such as when the overlay
   * is clicked. Useful if you are controling state externally
   */
  onClose?: () => void;
  /**
   * Will default the menu open
   */
  defaultIsOpen?: boolean;

  /**
   *
   * The offset of the menu bubble from the trigger
   */

  verticalOffset?: number;

  /**
   * The horizontal offset of the menu bubble from the trigger.
   */
  horizontalOffset?: number;

  /**
   * If true, will show a "done" button at the top of the action sheet
   * on mobile -- so the user can more easily close
   */
  showActionSheetDoneButton?: boolean;
  /**
   * If true, will ignore the action sheet mode and always open as a bubble
   */
  neverOpenAsActionSheet?: boolean;
};

export type MenuBaseComponentType = (props: MenuProps) => ReactElement;

/**
 * Accepts one of our standard content sizes
 * OR "matchTrigger". If set to match, it will
 * adjust to be the same width as the menu trigger.
 */
export type MenuContentSize =
  | "jumbotron"
  | "extraLarge"
  | "large"
  | "regular"
  | "small"
  | "matchTrigger";

export type MenuContentProps = {
  children: ReactElement;
  size?: MenuContentSize;
  maxHeight?: number | "100%";
  testID?: string;
  showVerticalScrollIndicator?: boolean;
};
type MenuContentComponentType = (props: MenuContentProps) => ReactElement;

export interface MenuComponent extends MenuBaseComponentType {
  Content: MenuContentComponentType;
}

export type MenuContextType = {
  onClose?: () => void;
  triggerWidth: number;
  isActionSheet: boolean;
  showActionSheetDoneButton?: boolean;
};

export const MenuContext = createContext<MenuContextType>({
  triggerWidth: 0,
  isActionSheet: false,
  showActionSheetDoneButton: false,
});
